import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../convex/_generated/api';
import { useUser } from '@clerk/clerk-react';
import { convex } from '../lib/convex';

/**
 * Hook to fetch user's credit balance from Convex with optimistic updates
 *
 * Optimistic updates allow immediate UI feedback when credits are deducted,
 * before the server confirms the deduction. This prevents the UI from showing
 * stale data until the next refresh.
 *
 * Usage:
 * 1. Call optimisticallyDeduct(amount) when submitting a job
 * 2. Balance will immediately reflect the deduction in UI
 * 3. When job completes, call refetch() to sync with actual server balance
 * 4. If files fail, the refetch will show the refunded amount
 */
export function useCredits() {
  const { user, isSignedIn } = useUser();
  const [creditData, setCreditData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Track optimistic deductions (amount deducted but not yet confirmed by server)
  const [optimisticDeduction, setOptimisticDeduction] = useState(0);

  // Track pending jobs to prevent duplicate optimistic deductions
  const pendingJobsRef = useRef<Set<string>>(new Set());

  // Explicit fetch function
  const fetchCredits = useCallback(async () => {
    if (!isSignedIn || !user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // No clerkId needed - automatically uses authenticated user from ctx.auth
      const data = await convex.query(api.credits.getBalance);

      setCreditData(data);
      console.log('💰 Credits fetched:', data);

      // Clear optimistic deduction when we get fresh data from server
      // Server data is the source of truth
      setOptimisticDeduction(0);
      pendingJobsRef.current.clear();

    } catch (err) {
      console.error('❌ Error fetching credits:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch credits'));
    } finally {
      setIsLoading(false);
    }
  }, [isSignedIn, user]);

  // Optimistically deduct credits before server confirmation
  const optimisticallyDeduct = useCallback((amount: number, jobId?: string) => {
    // Prevent duplicate deductions for the same job
    if (jobId && pendingJobsRef.current.has(jobId)) {
      console.log(`⏭️ Skipping duplicate optimistic deduction for job ${jobId}`);
      return;
    }

    if (jobId) {
      pendingJobsRef.current.add(jobId);
    }

    setOptimisticDeduction(prev => {
      const newDeduction = prev + amount;
      console.log(`🔮 Optimistic deduction: ${amount} credits (total optimistic: ${newDeduction})`);
      return newDeduction;
    });
  }, []);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Calculate displayed balance (actual - optimistic deductions)
  const actualBalance = creditData?.balance ?? 0;
  const displayedBalance = Math.max(0, actualBalance - optimisticDeduction);

  return {
    balance: displayedBalance, // Display optimistically updated balance
    actualBalance, // Expose actual server balance for debugging
    optimisticDeduction, // Expose optimistic amount for debugging
    totalGranted: creditData?.totalGranted ?? 0,
    totalConsumed: creditData?.totalConsumed ?? 0,
    lastUpdated: creditData?.lastUpdated ?? 0,
    isLoading,
    hasCredits: displayedBalance > 0,
    error,
    refetch: fetchCredits, // Refetch to sync with server (clears optimistic state)
    optimisticallyDeduct, // Deduct credits optimistically before server confirms
  };
}
